/**
 * Large stats section component: animates numbers counting up when they come into view.
 * Mounted on section with data-large-stats; uses data-large-stats-number for target numbers.
 * Also handles parallax effect on block background images.
 */
import Component from '../vendor/Component'

const PARALLAX_AMOUNT_MOBILE = 180
const PARALLAX_AMOUNT_DESKTOP = 260
const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)')
const SMALL_VIEWPORT_QUERY = window.matchMedia('(max-width: 767px)')

const animatedNumbers = []
const parallaxBlocks = []
let observerInstance = null
let scrollTicking = false
let scrollListenerAdded = false

function easeOutCustom (t) {
  return 1 - Math.pow(1 - t, 2.5)
}

function formatNumberWithCommas (num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function animateNumber (element, targetNumber, suffix) {
  const totalFrames = 120
  let currentFrame = 0

  function update () {
    currentFrame++

    const progress = currentFrame / totalFrames

    const easedProgress = easeOutCustom(progress)

    let currentValue
    if (targetNumber % 1 === 0) {
      currentValue = Math.floor(targetNumber * easedProgress)
    } else {
      currentValue = (targetNumber * easedProgress).toFixed(1)
    }

    const displayValue = targetNumber % 1 === 0
      ? formatNumberWithCommas(currentValue)
      : currentValue
    element.textContent = displayValue + suffix

    if (currentFrame < totalFrames) {
      requestAnimationFrame(update)
    } else {
      const finalValue = targetNumber % 1 === 0
        ? formatNumberWithCommas(targetNumber)
        : targetNumber
      element.textContent = finalValue + suffix
      element._largeStatsAnimated = true
    }
  }

  requestAnimationFrame(update)
}

function handleIntersection (entries) {
  entries.forEach(entry => {
    const element = entry.target

    if (entry.isIntersecting) {
      const numberString = element.getAttribute('data-large-stats-number')
      const targetNumber = parseFloat(numberString.replace(/,/g, ''))
      const suffix = element.getAttribute('data-large-stats-suffix') || ''

      if (!isNaN(targetNumber)) {
        element._largeStatsAnimated = false
        animateNumber(element, targetNumber, suffix)
      }
    } else {
      if (element._largeStatsAnimated) {
        const suffix = element.getAttribute('data-large-stats-suffix') || ''
        element.textContent = '0' + suffix
        element._largeStatsAnimated = false
      }
    }
  })
}

function initObserver () {
  if (observerInstance) {
    return observerInstance
  }

  observerInstance = new IntersectionObserver(handleIntersection, {
    root: null,
    rootMargin: '0px',
    threshold: 0.1
  })

  return observerInstance
}

function addNumbersForSection (sectionEl) {
  if (REDUCED_MOTION_QUERY.matches) {
    const numberElements = sectionEl.querySelectorAll('[data-large-stats-number]')
    numberElements.forEach(element => {
      const numberString = element.getAttribute('data-large-stats-number')
      const targetNumber = parseFloat(numberString.replace(/,/g, ''))
      const suffix = element.getAttribute('data-large-stats-suffix') || ''
      if (!isNaN(targetNumber)) {
        const displayValue = targetNumber % 1 === 0
          ? formatNumberWithCommas(targetNumber)
          : targetNumber
        element.textContent = displayValue + suffix
        element._largeStatsAnimated = true
      }
    })
    return
  }

  const observer = initObserver()
  const numberElements = sectionEl.querySelectorAll('[data-large-stats-number]')

  numberElements.forEach(element => {
    if (!element._largeStatsObserved) {
      element._largeStatsObserved = true
      animatedNumbers.push(element)
      observer.observe(element)
    }
  })
}

function removeNumbersForSection (sectionEl) {
  if (!observerInstance) return

  const numberElements = sectionEl.querySelectorAll('[data-large-stats-number]')
  numberElements.forEach(element => {
    if (element._largeStatsObserved) {
      observerInstance.unobserve(element)
      element._largeStatsObserved = false
      element._largeStatsAnimated = false

      const index = animatedNumbers.indexOf(element)
      if (index !== -1) {
        animatedNumbers.splice(index, 1)
      }
    }
  })
}

function getElementOffsetTop (element) {
  const rect = element.getBoundingClientRect()
  return rect.top + (window.scrollY ?? window.pageYOffset)
}

function applyParallaxTransforms () {
  const scrollY = window.scrollY ?? window.pageYOffset
  const viewportHeight = window.innerHeight
  const amount = SMALL_VIEWPORT_QUERY.matches ? PARALLAX_AMOUNT_MOBILE : PARALLAX_AMOUNT_DESKTOP

  parallaxBlocks.forEach(({ blockElement, target }) => {
    const blockHeight = blockElement.getBoundingClientRect().height || blockElement.offsetHeight
    if (!blockHeight) return

    const blockTop = getElementOffsetTop(blockElement)
    const start = blockTop - viewportHeight
    const end = blockTop + blockHeight

    let translateY
    if (scrollY <= start) {
      translateY = -amount / 2
    } else if (scrollY >= end) {
      translateY = amount / 2
    } else {
      const progress = (scrollY - start) / (end - start)
      translateY = amount * progress - amount / 2
    }

    const value = `translate3d(0, ${Math.round(translateY)}px, 0)`
    target.style.transform = value
  })
}

function onScroll () {
  if (!scrollTicking) {
    scrollTicking = true
    requestAnimationFrame(() => {
      scrollTicking = false
      applyParallaxTransforms()
    })
  }
}

function ensureScrollListener () {
  if (parallaxBlocks.length === 0 || scrollListenerAdded) return
  scrollListenerAdded = true
  window.addEventListener('scroll', onScroll, { passive: true })
}

function removeScrollListener () {
  if (parallaxBlocks.length > 0) return
  scrollListenerAdded = false
  window.removeEventListener('scroll', onScroll)
}

function addParallaxBlocksForSection (sectionEl) {
  if (REDUCED_MOTION_QUERY.matches) return

  const blockElements = sectionEl.querySelectorAll('[data-large-stats-el="block"]')

  blockElements.forEach((blockEl) => {
    const wrapper = blockEl.querySelector('[data-large-stats-parallax-wrapper]')
    if (!wrapper || wrapper._largeStatsParallaxInit) return

    const target = wrapper.querySelector('.sec-LargeStats_BlockImageInner')
    if (!target) return

    wrapper._largeStatsParallaxInit = true
    parallaxBlocks.push({ blockElement: blockEl, target })
  })

  if (parallaxBlocks.length > 0) {
    ensureScrollListener()
    applyParallaxTransforms()
  }
}

function destroyParallaxForSection (sectionEl) {
  if (!sectionEl) return

  const toRemove = parallaxBlocks.filter((entry) => sectionEl.contains(entry.blockElement))
  toRemove.forEach((entry) => {
    const wrapper = entry.blockElement.querySelector('[data-large-stats-parallax-wrapper]')
    if (wrapper) wrapper._largeStatsParallaxInit = false
    entry.target.style.transform = ''
  })

  toRemove.forEach((entry) => {
    const i = parallaxBlocks.indexOf(entry)
    if (i !== -1) parallaxBlocks.splice(i, 1)
  })

  removeScrollListener()
}

function initLargeStats () {
  document.querySelectorAll('[data-large-stats]').forEach((section) => {
    addNumbersForSection(section)
    addParallaxBlocksForSection(section)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLargeStats)
} else {
  initLargeStats()
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target
  if (!section.hasAttribute('data-large-stats') && !section.classList.contains('sec-LargeStats')) return
  addNumbersForSection(section)
  addParallaxBlocksForSection(section)
})

document.addEventListener('shopify:section:unload', (event) => {
  removeNumbersForSection(event.target)
  destroyParallaxForSection(event.target)
})

export default class LargeStats extends Component {
  constructor(elem, theme, options, ctx) {
    super(elem, theme, options, ctx)
  }

  async mount () {
    addNumbersForSection(this._elem)
    addParallaxBlocksForSection(this._elem)
  }

  unmount () {
    removeNumbersForSection(this._elem)
    destroyParallaxForSection(this._elem)
  }
}
